/* eslint-disable react/display-name */
import { Box, Button, Container, styled } from '@mui/material';
import { useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import BreadcrumbWithBtnBack from 'src/components/BreadcrumbWithBtnBack';
import TabComponent from 'src/components/Tab/TabComponent';
import ComingSoon from 'src/features/CompanyDetail/Common/ComingSoon';
import TradeDataInformation from 'src/features/TradeData/Common/Information';
import TradeDataTab from 'src/features/TradeData/TradeData';

// Utilities
import dayjs from 'dayjs';
import MarketAnalyticsPlaceHolder from 'src/asset/images/trade-data/MarketAnalytics.png';
import SupplierCountryPlaceHolder from 'src/asset/images/trade-data/SupplierCountry.png';
import { ID_COMPONENT_TRADE, TABS_TRADE_DATA, TypeTabTradeData } from 'src/features/TradeData/config';
import MarketAnalytics from 'src/features/TradeData/MarketAnalytics';

import { Sliders } from 'react-feather';
import IconFeather from 'src/components/common/Icon';
import { SectionErrorBoundary } from 'src/components/ErrorBoundary/SectionErrorBoundary';
import AverageUnitPrice from 'src/features/TradeData/AverageUnitPrice';
import SupplierBuyer from 'src/features/TradeData/SupplierBuyer';
import SupplierBuyerCountry from 'src/features/TradeData/SupplierBuyerCountry';
import UnitPriceSupplierBuyer from 'src/features/TradeData/UnitPrices';
import ViewByPorts from 'src/features/TradeData/ViewByPorts';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { FORMAT_DATE_TIME } from 'src/utils/constants/config';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import { ISearchGlobalTradeDataQueryParams } from 'src/utils/typings/dto/tradeData';
import { FormType, useFormSearch } from 'src/zustand/useFormSearch';

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(7),
  marginTop: theme.spacing(1),
  textAlign: 'center',
}));

const TradeData = () => {
  const queryParams = useQueryParams() as ISearchGlobalTradeDataQueryParams;
  const { handleOpenSearch } = useFormSearch((state) => state);
  const currentTab = queryParams.tab || TypeTabTradeData.BuyerCountry;

  const navigate = useNavigate();

  const { hasAccess, usageInformation } = useFeatureAccess();

  const tabsTradeDataFiltered = useMemo(() => {
    return TABS_TRADE_DATA.filter((tab) => hasAccess(tab.endpoints));
  }, [hasAccess]);

  useLayoutEffect(() => {
    if (usageInformation?.data?.id === 0) return;
    const isCurrentTabExist = tabsTradeDataFiltered.some((t) => t.key === currentTab);
    if (!isCurrentTabExist && tabsTradeDataFiltered.length > 0) {
      const firstAvailableTab = tabsTradeDataFiltered[0];
      navigate(
        {
          pathname: AuthRouteKeys.TradeData,
          search: handleQueryParamFromObject({ ...queryParams, tab: firstAvailableTab.key }),
        },
        { replace: true },
      );
    }
  }, [currentTab, tabsTradeDataFiltered, navigate, queryParams, usageInformation?.data?.id]);

  const PortOfOrigin = useMemo(() => {
    return () => <ViewByPorts selectedTab={TypeTabTradeData.PortOrigin} />;
  }, []);

  const PortOfDelivery = useMemo(() => {
    return () => <ViewByPorts selectedTab={TypeTabTradeData.PortDelivery} />;
  }, []);

  const Supplier = useMemo(() => {
    return () => <SupplierBuyer selectedTab={TypeTabTradeData.Suppliers} />;
  }, []);

  const Buyer = useMemo(() => {
    return () => <SupplierBuyer selectedTab={TypeTabTradeData.Buyers} />;
  }, []);

  const renderForm = () => {
    switch (currentTab) {
      case TypeTabTradeData.TradeData:
        return <TradeDataTab />;
      case TypeTabTradeData.MarketAnalytics:
        return <MarketAnalytics />;
      case TypeTabTradeData.MarketForecasts:
        return <ComingSoon image={MarketAnalyticsPlaceHolder} />;
      case TypeTabTradeData.RouteMarket:
        return <ComingSoon image={SupplierCountryPlaceHolder} />;
      case TypeTabTradeData.SupplierCountry:
        return <SupplierBuyerCountry selectedTab={currentTab} />;
      case TypeTabTradeData.BuyerCountry:
        return <SupplierBuyerCountry selectedTab={currentTab} />;
      case TypeTabTradeData.PortOrigin:
        return <PortOfOrigin />;
      case TypeTabTradeData.PortDelivery:
        return <PortOfDelivery />;
      case TypeTabTradeData.Suppliers:
        return <Supplier />;
      case TypeTabTradeData.Buyers:
        return <Buyer />;
      case TypeTabTradeData.UnitPriceBuyers:
        return <UnitPriceSupplierBuyer selectedTab={currentTab} />;
      case TypeTabTradeData.UnitPriceSuppliers:
        return <UnitPriceSupplierBuyer selectedTab={currentTab} />;
      case TypeTabTradeData.AverageUnitPrice:
        return <AverageUnitPrice />;
      default: {
        return <TradeDataTab />;
      }
    }
  };

  return (
    <StyledContainer maxWidth="xl">
      <BreadcrumbWithBtnBack
        listItem={[
          {
            text: 'Trade Data',
          },
          {
            text: queryParams.hs_code,
          },
          {
            text: `${dayjs(queryParams.start_date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE)} - ${dayjs(queryParams.end_date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE)}`,
          },
        ]}
        updatedAt="03-08-2009T00:00:00Z"
      />
      <SectionErrorBoundary resetKey="TradeDataInformation" sectionName="TradeDataInformation">
        <TradeDataInformation />
      </SectionErrorBoundary>
      <TabComponent
        tabs={tabsTradeDataFiltered}
        selected={currentTab}
        handleChange={(e, value) => {
          navigate({
            pathname: AuthRouteKeys.TradeData,
            search: handleQueryParamFromObject({ ...queryParams, ...{ tab: value } }),
          });
        }}
      />
      {/* eslint-disable-next-line react/jsx-no-undef */}
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Button
          onClick={() => {
            handleOpenSearch(FormType.SearchTradeData);
          }}
          color="primary"
          startIcon={<IconFeather muiProps={{ fontSize: 'small' }} icon={<Sliders />} />}
          variant="outlined"
        >
          Refine your search
        </Button>
      </Box>

      <Box mt={2} />
      <Box id={ID_COMPONENT_TRADE}>
        <SectionErrorBoundary resetKey={currentTab} sectionName={`TradeData - ${currentTab}`}>
          {renderForm()}
        </SectionErrorBoundary>
      </Box>
    </StyledContainer>
  );
};

export default TradeData;
